import { CircularProgress, Collapse, Grid, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import React from 'react';

import {
  createJobPreset,
  createJobPresetVersion,
  setJobPresetGlobal,
  setJobPresetPublished,
  updateJobPreset,
} from 'api/jobPreset';
import PresetActionButtons from 'components/atoms/PresetSection/PresetActionButtons';
import SavePreset from 'components/atoms/PresetSection/SavePreset';
import { JobPreset } from 'interfaces/jobPreset';
import { Permission } from 'interfaces/permissionOption';
import { ModelPreferencesById } from 'utils/models';
import { useCasesParams } from 'utils/useCasesParams';
import { usePermissions } from 'utils/usePermissions';
import { NewCell } from '..';
import {
  createNewPresetJson,
  createPresetJsonFromCurrentState,
  HighlightedPresetInput,
  PresetSaveData,
} from './presetUtils';

const SNACK_BAR_KEY_SAVE_PRESET = 'SAVE_PRESET';
const SNACK_BAR_KEY_UPDATE_PRESET = 'UPDATE_PRESET';

export interface SaveUpdatePresetButtonsProps {
  currentPreset: JobPreset | null;
  currentStains: string[];
  pipRunName: string;
  modelPreferencesByModelId: ModelPreferencesById;
  postProcessingActionsById: Record<string, any>;
  postProcessingActions: any[];
  featureActions: any[];
  newCellsCreated: Record<string, NewCell>;
  visualizations: any[];
  gridBasedCalculationParams: Record<string, any>;
  onPresetChange?: (preset: JobPreset) => void;
  highlightedInputs: HighlightedPresetInput[];
}

export const SaveUpdatePresetButtons: React.FC<SaveUpdatePresetButtonsProps> = ({
  currentPreset,
  currentStains,
  pipRunName,
  modelPreferencesByModelId,
  postProcessingActionsById,
  postProcessingActions,
  featureActions,
  newCellsCreated,
  visualizations,
  gridBasedCalculationParams,
  onPresetChange,
  highlightedInputs,
}) => {
  const { casesParams } = useCasesParams();
  const [isAddingPreset, setIsAddingPreset] = React.useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();

  const handlePresetSaved = (preset: JobPreset) => {
    onPresetChange?.(preset);
    queryClient.invalidateQueries({ queryKey: ['jobPresets'] });
    queryClient.invalidateQueries({ queryKey: ['jobPresetVersions', preset.presetId] });
  };

  const createJobPresetMutation = useMutation(createJobPreset, {
    onSuccess: (preset) => {
      handlePresetSaved(preset);
      enqueueSnackbar('job preset saved', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Error occurred, save job preset failed', {
        variant: 'error',
      });
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_SAVE_PRESET);
    },
  });

  const createJobPresetVersionMutation = useMutation(createJobPresetVersion, {
    onSuccess: (preset) => {
      handlePresetSaved(preset);
      enqueueSnackbar('preset version saved', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Error occurred, save preset version failed', {
        variant: 'error',
      });
    },
  });

  const publishedMutation = useMutation(setJobPresetPublished, {
    onSuccess: handlePresetSaved,
    onError: () => enqueueSnackbar('Error occurred, update preset publication failed', { variant: 'error' }),
  });
  const globalMutation = useMutation(setJobPresetGlobal, {
    onSuccess: handlePresetSaved,
    onError: () => enqueueSnackbar('Error occurred, update preset global visibility failed', { variant: 'error' }),
  });

  const updateJobPresetMutation = useMutation(updateJobPreset, {
    onSuccess: (preset) => {
      handlePresetSaved(preset);
      enqueueSnackbar('job preset saved', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Error occurred, update job preset failed', {
        variant: 'error',
      });
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_UPDATE_PRESET);
    },
  });

  const presetSaveData: PresetSaveData = {
    postProcessingActionsById,
    postProcessingActions,
    featureActions,
    newCellsCreated,
    visualizations,
    gridBasedCalculationParams,
    modelPreferencesByModelId,
    pipRunName,
    highlightedInputs,
  };

  const presetJsonUpdates = createPresetJsonFromCurrentState(presetSaveData);

  const saveUpdateCommonProps = {
    stains: currentStains,
    steps: ['calculate_features'],
    sourceStudyId: casesParams?.studyId,
  };

  const savePreset = (name: string) => {
    createJobPresetMutation.mutate({
      name,
      ...saveUpdateCommonProps,
      presetJson: createNewPresetJson(presetSaveData),
    });
    setIsAddingPreset(false);
    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Saving preset...</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_SAVE_PRESET,
      autoHideDuration: null,
    });
  };

  const updatePreset = () => {
    if (!currentPreset) return;

    updateJobPresetMutation.mutate({
      id: currentPreset.id,
      ...saveUpdateCommonProps,
      presetJson: presetJsonUpdates,
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Updating preset...</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_UPDATE_PRESET,
      autoHideDuration: null,
    });
  };

  const saveAsVersion = () => {
    if (!currentPreset) return;

    createJobPresetVersionMutation.mutate({
      id: currentPreset.id,
      ...saveUpdateCommonProps,
      presetJson: presetJsonUpdates,
    });
  };

  return (
    <>
      <Collapse in={!isAddingPreset}>
        <PresetActionButtons
          setIsAddingPreset={setIsAddingPreset}
          selectedPreset={currentPreset}
          onUpdatePreset={currentPreset?.deletedAt || currentPreset?.isPublished ? undefined : updatePreset}
          onSavePresetVersion={currentPreset ? saveAsVersion : undefined}
          onTogglePublished={
            currentPreset && hasPermission(Permission.PublishCalculateFeaturesPreset)
              ? () => publishedMutation.mutate({ id: currentPreset.id, isPublished: !currentPreset.isPublished })
              : undefined
          }
          onToggleGlobal={
            currentPreset && hasPermission(Permission.MakeCalculateFeaturesPresetGlobal)
              ? () => globalMutation.mutate({ id: currentPreset.id, isGlobal: !currentPreset.isGlobal })
              : undefined
          }
          updateLabel="Save"
          saveVersionLabel="Save as version"
          createLabel="Save as preset..."
          publishedLabel={currentPreset?.isPublished ? 'Unpublish' : 'Publish'}
          globalLabel={currentPreset?.isGlobal ? 'Make study-specific' : 'Make global'}
        />
      </Collapse>
      <SavePreset
        isAddingPreset={isAddingPreset}
        setIsAddingPreset={setIsAddingPreset}
        isLoading={false}
        savePreset={savePreset}
      />
    </>
  );
};
